import {
  Alert,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { ConveyedPersonAction, PartyReach } from "@thalamos/haven-common";
import { hasInvolvementEnded } from "@thalamos/haven-common";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/**
 * What an involvement conveys, as chips.
 *
 * The actions are the server's answer, rendered — never worked out here. The
 * page and the gate that refuses a request call one function between them, so a
 * grid that computed its own would be a second implementation of the policy,
 * and its failure mode is that it is reassuring and wrong.
 *
 * An involvement conveying nothing shows a chip saying so rather than an empty
 * space, because "this party can do nothing" and "we did not work it out" must
 * not look the same.
 */
const Conveys = ({ actions }: { actions: readonly ConveyedPersonAction[] }) => {
  const { t } = useTranslation();

  if (actions.length === 0) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t("access.conveysNothing")}
        sx={{ borderStyle: "dashed", color: "text.secondary" }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {actions.map((action) => {
        const description = t(`access.actionDescription.${action}`);

        return (
          // One word cannot tell "contribute" from "complete", and a reader
          // deciding whether somebody may finish a child's assessment needs to
          // know which they hold. The sentence is on the chip's accessible name
          // as well as in the tooltip, so it is not hover-only.
          <Tooltip key={action} title={description} enterDelay={400}>
            <Chip
              size="small"
              variant="outlined"
              label={t(`access.action.${action}`)}
              aria-label={`${t(`access.action.${action}`)}: ${description}`}
              // `read` is what everybody has; the rest are what distinguishes
              // one involvement from another, so those carry the colour.
              color={action === "read" ? "default" : "primary"}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export type ReachGridProps = {
  reach: PartyReach[] | undefined;
  failed: boolean;
};

/**
 * Every young person one account reaches, and the involvement that gives them
 * each. A table, because the list is scanned down one column: which child.
 */
export const ReachGrid = ({ reach, failed }: ReachGridProps) => {
  const { t } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" component="h2">
        {t("access.reachTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("access.reachDescription")}
      </Typography>

      {failed ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("access.loadFailed")}
        </Alert>
      ) : null}

      {reach !== undefined && reach.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("access.reachEmpty")}
        </Alert>
      ) : null}

      {reach !== undefined && reach.length > 0 ? (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("access.column.person")}</TableCell>
                <TableCell>{t("access.column.involvement")}</TableCell>
                <TableCell>{t("access.column.since")}</TableCell>
                <TableCell>{t("access.column.conveys")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reach.map((entry) => (
                <TableRow
                  key={`${entry.personId}-${entry.kind}-${entry.startedAt.toISOString()}`}
                  sx={{ opacity: hasInvolvementEnded(entry) ? 0.6 : 1 }}
                >
                  <TableCell>{entry.personLabel}</TableCell>
                  <TableCell>{t(`access.kind.${entry.kind}`)}</TableCell>
                  {/* A date that wraps to three lines is what a narrow column
                      does to one; this one keeps its width instead. */}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {CALENDAR_DATE.format(entry.startedAt)}
                  </TableCell>
                  <TableCell>
                    <Conveys actions={entry.actions} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Paper>
  );
};
